import { ChevronDown, Plus } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useCashier } from "./CashierProvider";
import { WalletMenu, walletCurrencyIcon } from "./WalletMenu";

const FOCUSABLE =
  'input:not(:disabled), button:not(:disabled):not([tabindex="-1"]), [href], [tabindex]:not([tabindex="-1"])';

export function WalletControl() {
  const {
    state,
    message,
    wallets,
    selectedWallet,
    totalBalance,
    busy,
    openCashier,
    selectWallet,
  } = useCashier();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const close = useCallback((restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() => triggerRef.current?.focus());
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const mobile = window.matchMedia("(max-width: 479px)").matches;
    const previousOverflow = document.body.style.overflow;
    if (mobile) document.body.style.overflow = "hidden";
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        close();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close(true);
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = [
        ...panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      ];
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === panelRef.current) {
        event.preventDefault();
        last.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    window.requestAnimationFrame(() => {
      panelRef.current?.focus();
    });
    return () => {
      if (mobile) document.body.style.overflow = previousOverflow;
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [close, open]);

  const balanceText = selectedWallet
    ? `${selectedWallet.stale ? "~" : ""}${selectedWallet.total}`
    : (message ?? "Balance unavailable");
  const accessibleBalance = selectedWallet
    ? `${selectedWallet.currency} balance ${selectedWallet.total}${
        selectedWallet.stale ? ", temporarily stale" : ""
      }`
    : balanceText;
  const title = selectedWallet
    ? [
        `Cash ${selectedWallet.cash ?? "unavailable"}`,
        `Bonus ${selectedWallet.bonus ?? "unavailable"}`,
        `Locked ${selectedWallet.locked ?? "unavailable"}`,
        selectedWallet.sourceLabel,
      ].join(" · ")
    : balanceText;
  const triggerIcon = selectedWallet
    ? walletCurrencyIcon(selectedWallet)
    : null;
  const amountSize =
    balanceText.length > 13
      ? "compact"
      : balanceText.length > 10
        ? "tight"
        : "normal";

  return (
    <div className="cashier-wallet-control" data-balance-visibility="required">
      <button
        ref={triggerRef}
        aria-controls="cashier-wallet-menu"
        aria-expanded={open}
        aria-label={`Open wallet, ${accessibleBalance}`}
        className="cashier-wallet-trigger"
        data-amount-length={balanceText.length}
        data-amount-size={amountSize}
        data-state={state}
        onClick={() => setOpen((current) => !current)}
        title={title}
        type="button"
      >
        <span className="cashier-wallet-trigger__icon" aria-hidden>
          {triggerIcon ? (
            <img alt="" src={triggerIcon} />
          ) : (
            <span>{selectedWallet?.currency.slice(0, 1) ?? "?"}</span>
          )}
        </span>
        <span className="cashier-wallet-trigger__value">
          <strong>{balanceText}</strong>
          {selectedWallet ? (
            <span className="cashier-wallet-trigger__currency">
              {selectedWallet.currency}
            </span>
          ) : null}
        </span>
        <ChevronDown aria-hidden />
      </button>

      <button
        aria-label="Open Cashier"
        className="cashier-launch-button"
        onClick={(event) => openCashier("deposit", event.currentTarget)}
        type="button"
      >
        <Plus aria-hidden className="cashier-launch-button__mobile-icon" />
        <img
          alt=""
          aria-hidden
          className="cashier-launch-button__desktop-icon"
          src="/app/static/themes/monkeytilt/icons/cashier.svg"
        />
        <span>Cashier</span>
      </button>

      {open ? (
        <>
          <button
            aria-label="Close wallet"
            className="cashier-wallet-menu__scrim"
            onClick={() => close(true)}
            tabIndex={-1}
            type="button"
          />
          <WalletMenu
            busy={busy}
            message={message}
            onClose={() => close(true)}
            onOpenCashier={() => {
              close();
              openCashier("deposit", triggerRef.current);
            }}
            onSelectWallet={selectWallet}
            panelRef={panelRef}
            selectedWallet={selectedWallet}
            totalBalance={totalBalance}
            wallets={wallets}
          />
        </>
      ) : null}
    </div>
  );
}
